import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { ENVIRONMENT_LABEL, label } from '../lib/labels.js';
import { dateTime } from '../lib/format.js';
import {
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Panel,
  StatusBadge,
} from '../components/ui.js';

interface TokenItem {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  owner: { id: string; email: string; name: string };
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  status: 'active' | 'expired' | 'revoked';
}

interface TokensData {
  items: TokenItem[];
  scopes: Array<{ id: string; label: string }>;
  maxDays: number;
  environment: string;
}

interface Created {
  token: string;
  name: string;
}

const STATUS: Record<TokenItem['status'], { tone: 'ok' | 'neu' | 'bad'; text: string }> = {
  active: { tone: 'ok', text: 'platný' },
  expired: { tone: 'neu', text: 'vypršel' },
  revoked: { tone: 'bad', text: 'zrušený' },
};

/** Nastavení → Přístup pro MCP: osobní tokeny pro Claude a další klienty MCP (jen administrátor). */
export function McpTokensTab({ meId }: { meId: string }): JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Created | null>(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState<TokenItem | null>(null);
  const tokenInput = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<{
    name: string;
    userId: string;
    days: number;
    scopes: string[];
  }>({ name: '', userId: meId, days: 90, scopes: ['read'] });

  const list = useQuery<TokensData>({
    queryKey: ['mcp-tokens'],
    queryFn: () => api.get('/api/mcp-tokens'),
  });
  const users = useQuery<{
    items: Array<{ id: string; email: string; name: string; active: boolean }>;
  }>({ queryKey: ['users'], queryFn: () => api.get('/api/users') });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ token: string }>('/api/mcp-tokens', {
        name: form.name,
        userId: form.userId,
        scopes: form.scopes,
        days: form.days,
      }),
    onSuccess: (res) => {
      setError(null);
      setCopied(false);
      setCreated({ token: res.token, name: form.name });
      setForm({ ...form, name: '' });
      void client.invalidateQueries({ queryKey: ['mcp-tokens'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/api/mcp-tokens/${id}`),
    onSuccess: () => {
      setError(null);
      setRevoking(null);
      void client.invalidateQueries({ queryKey: ['mcp-tokens'] });
    },
    onError: (err: Error) => {
      setRevoking(null);
      setError(err.message);
    },
  });

  if (list.isLoading) return <LoadingState what="tokeny" />;
  if (list.isError || !list.data)
    return (
      <ErrorState
        error={list.error}
        title="Tokeny se nepodařilo načíst"
        onRetry={() => void list.refetch()}
      />
    );
  const data = list.data;
  const envName = label(ENVIRONMENT_LABEL, data.environment);

  const toggle = (id: string): void =>
    setForm({
      ...form,
      scopes: form.scopes.includes(id) ? form.scopes.filter((s) => s !== id) : [...form.scopes, id],
    });
  const daysBad = !Number.isInteger(form.days) || form.days < 1 || form.days > data.maxDays;

  const copy = async (): Promise<void> => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.token);
      setCopied(true);
    } catch {
      tokenInput.current?.select();
      setCopied(false);
    }
  };
  const closeCreated = (): void => {
    setCreated(null);
    setCopied(false);
  };

  return (
    <>
      {error ? <ErrorState error={new Error(error)} title="Změna se nepovedla" /> : null}
      <Panel
        title="Přístup pro MCP"
        subtitle={`Osobní tokeny, kterými se Claude (Claude Code, desktopová aplikace) připojí k hubu na adrese /mcp. Tokeny platí jen v prostředí ${envName}; každé prostředí má vlastní.`}
      >
        <p className="hint">
          Token jedná jako svůj vlastník: smí jen to, co smí jeho oddělení, a ještě jen to, co
          povolují jeho oprávnění. Přes MCP nelze ostře odesílat zprávy ani přepínat režim odesílání
          a prostředí. Každé volání se zapisuje do Auditu změn.
        </p>
        {data.items.length === 0 ? (
          <EmptyState title="Zatím žádný token" icon="plug">
            Vytvořte první token ve formuláři níže.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Název</th>
                  <th>Vlastník</th>
                  <th>Oprávnění</th>
                  <th>Platí do</th>
                  <th>Naposledy použit</th>
                  <th>Stav</th>
                  <th>
                    <span className="sr-only">Akce</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((t) => (
                  <tr key={t.id}>
                    <td>
                      {t.name}
                      <br />
                      <small>{t.prefix}…</small>
                    </td>
                    <td>{t.owner.email}</td>
                    <td>{t.scopes.join(', ')}</td>
                    <td>{dateTime(t.expiresAt)}</td>
                    <td>{dateTime(t.lastUsedAt)}</td>
                    <td>
                      <StatusBadge tone={STATUS[t.status].tone}>
                        {STATUS[t.status].text}
                      </StatusBadge>
                    </td>
                    <td>
                      {t.status === 'active' ? (
                        <button type="button" className="btn" onClick={() => setRevoking(t)}>
                          Zrušit
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Nový token" subtitle="Token se zobrazí jen jednou, hned po vytvoření.">
        <div className="s2c-form">
          <Field label="Název" hint="Třeba „Claude Code, Daniel“.">
            <input
              type="text"
              autoComplete="off"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field label="Vlastník" hint="Token má práva tohoto uživatele.">
            <select
              value={form.userId}
              onChange={(e) => setForm({ ...form, userId: e.target.value })}
            >
              {(users.data?.items ?? [])
                .filter((u) => u.active)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
            </select>
          </Field>
          <Field
            label="Platnost ve dnech"
            hint={`Povinná, nejvýš ${data.maxDays} dní.`}
            error={daysBad ? `Zadejte celé číslo od 1 do ${data.maxDays}.` : null}
          >
            <input
              type="number"
              min={1}
              max={data.maxDays}
              value={form.days}
              onChange={(e) => setForm({ ...form, days: Number(e.target.value) })}
            />
          </Field>
        </div>
        <fieldset className="s2c-fieldset">
          <legend>Oprávnění</legend>
          {data.scopes.map((s) => (
            <div key={s.id}>
              <label className="s2c-nowrap">
                <input
                  type="checkbox"
                  checked={form.scopes.includes(s.id)}
                  disabled={s.id === 'read'}
                  onChange={() => toggle(s.id)}
                />{' '}
                {s.label}
              </label>
            </div>
          ))}
        </fieldset>
        <div className="s2c-actions">
          <button
            type="button"
            className="btn pri"
            disabled={form.name.trim().length === 0 || daysBad || create.isPending}
            onClick={() => create.mutate()}
          >
            Vytvořit token
          </button>
        </div>
      </Panel>

      <Dialog open={created !== null} onClose={closeCreated} title="Token je vytvořen" wide>
        <div className="dlg-content">
          <p>
            <b>Zkopírujte si ho teď.</b> Hub si pamatuje jen jeho otisk, takže se už nikdy
            nezobrazí. Při ztrátě token zrušte a vytvořte nový.
          </p>
          <Field label={`Token „${created?.name ?? ''}“`}>
            <input
              ref={tokenInput}
              type="text"
              readOnly
              value={created?.token ?? ''}
              onFocus={(e) => e.currentTarget.select()}
            />
          </Field>
          <p className="hint">
            Do Claude Code ho přidáte příkazem uvedeným v dokumentu docs/MCP.md (hlavička
            Authorization: Bearer a tento token). Nikomu ho neposílejte e-mailem.
          </p>
        </div>
        <div className="row dlg-actions">
          <button type="button" className="btn pri" onClick={() => void copy()}>
            {copied ? 'Zkopírováno' : 'Kopírovat'}
          </button>
          <button type="button" className="btn" onClick={closeCreated}>
            Hotovo
          </button>
        </div>
      </Dialog>

      <ConfirmDialog
        open={revoking !== null}
        title="Zrušit token?"
        confirmLabel="Zrušit token"
        danger
        busy={revoke.isPending}
        onCancel={() => setRevoking(null)}
        onConfirm={() => {
          if (revoking) revoke.mutate(revoking.id);
        }}
      >
        <p>
          Token <b>{revoking?.name}</b> přestane okamžitě platit a klient, který ho používá, se už
          nepřipojí. Akci nelze vrátit zpět; potřebujete-li přístup, vytvořte nový token.
        </p>
      </ConfirmDialog>
    </>
  );
}
